import React from 'react';

import { gql } from '@soundxyz/gql-string';
import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { DeleteShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/DeleteShelfMenuItem';
import { EditShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/EditShelfMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { PostPlaylistMenuItem } from '@components/basics/Dropdowns/PostPlaylistMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { DeleteShelfModal } from '@components/modals/DeleteShelfModal';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType, ShelfType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { PlaylistPreviewDropdownInfoFragmentDoc } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PlaylistPreviewDropdownInfo on Shelf {
    id
    ...PlaylistShareInfo
  }
`);

export const PreviewDropdown = ({
  isDropdownOpen,
  setIsDropdownOpen,
  playlist,
  trackIds,
  playlistLink,
  isOwner,
  playlistType,
  variant,
}: {
  isDropdownOpen: boolean;
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  trackIds: string[];
  playlistLink: string;
  isOwner: boolean;
  playlistType: ShelfType;
  playlist: FragmentType<PlaylistPreviewDropdownInfoFragmentDoc>;
  variant: 'inverted' | 'tertiary';
}) => {
  const playlistInfo = getFragment(PlaylistPreviewDropdownInfoFragmentDoc, playlist);

  const playlistId = playlistInfo.id;

  const { openModal } = ModalContext.useContainer();
  const { desktop } = MediaQuery.useContainer();
  const isUserCreated = playlistType === 'DEFAULT';

  return (
    <ThreeDotsDropdown
      isDropdownOpen={isDropdownOpen}
      setIsDropdownOpen={setIsDropdownOpen}
      dropdownProps={{
        align: !desktop ? 'end' : 'start',
      }}
      variant={variant}
    >
      <AddToQueueMenuItem
        trackIds={trackIds}
        disabled={!trackIds}
        emitAnalytics={() => {
          trackClickEvent({
            event: 'Add to Queue Clicked',
            properties: {
              associationId: playlistId,
              associationType: 'Playlist',
              sourceComponent: 'Playlist Preview',
            },
          });
        }}
      />

      <PostPlaylistMenuItem playlistId={playlistId} source="Playlist Card" />

      <ShareMenuItem playlist={playlistInfo} source="Playlist Preview" />
      {isOwner && (
        <>
          {isUserCreated && (
            <EditShelfMenuItem label="Edit Playlist" shelfWebappUri={playlistLink} />
          )}
          <DeleteShelfMenuItem
            onClick={() =>
              openModal(ModalType.DELETE_SHELF, {
                body: <DeleteShelfModal shelfId={playlistId} />,
              })
            }
          />
        </>
      )}
    </ThreeDotsDropdown>
  );
};
